/**
 * The shared API client every portal subclasses.
 *
 * Framework-free and `fetch`-injected, so the parts that are easy to get wrong
 * — error handling and the report envelope — are testable without a browser.
 *
 * Two things this client refuses to do, both of which are the normal thing for
 * a client to do:
 *
 *   It does not collapse errors into a message. API Specification §1.6 puts
 *   `rule_id` in the error body precisely so an affiliate can look up *why*
 *   rather than guess, and a client that renders "Something went wrong" throws
 *   away the one field the specification exists to carry.
 *
 *   It does not unwrap the report envelope. §39.4's `is_provisional` travels
 *   with the data rather than beside it; a client returning `body.data` hands
 *   the UI figures with the caveat stripped off.
 */

import { passwordRefusalText } from './password-policy.ts';
import { maybeNotifyRequestSuccess, showErrorToast } from './request-success.ts';
import { maybeNotifySessionExpired } from './session-expired.ts';
import { stripRuleReferences, stripRuleReferencesDeep } from './strip-rule-references.ts';

/**
 * Must match `apps/api-core/src/cookies.ts`'s `CSRF_HEADER`/`CSRF_VALUE`.
 * Duplicated rather than imported — a package may not depend on an app
 * (check-boundaries.mjs) — the same way every route path here is already a
 * string literal duplicated from server.ts's own route table.
 */
export const CSRF_HEADER = 'x-affdice-csrf';
export const CSRF_VALUE = '1';

/**
 * How long a read waits for an answer before giving up on it.
 *
 * `fetch` has no deadline of its own: a server that accepts the connection and
 * then stops answering leaves the promise pending for as long as the tab is
 * open. `useLoad` never leaves `loading`, so the screen shows its loading
 * skeletons indefinitely — no message, no retry, and nothing to tell the
 * reader the figures are not merely slow. All three consoles did exactly that
 * for eleven minutes on staging.
 *
 * Generous rather than snappy, because the alternative failure is worse: a
 * report over a wide range legitimately takes seconds, and a deadline that
 * cuts it off turns a slow screen into a broken one.
 */
export const READ_TIMEOUT_MS = 30_000;

/**
 * The same, for a file. A CSV or a PDF is built server-side over a range the
 * reader chose, so it is allowed to take considerably longer than a screen.
 */
export const FILE_TIMEOUT_MS = 120_000;

/** The boot-time cookie probe — see `session()` for why it is shorter. */
export const SESSION_TIMEOUT_MS = 10_000;

/**
 * A deadline, where the runtime has one.
 *
 * `AbortSignal.timeout` is in every browser this ships to; the fallback is for
 * a test runner or an embedder that injects its own `fetch` without it.
 */
function deadline(ms: number): AbortSignal | undefined {
  const timeout = (AbortSignal as { timeout?: (ms: number) => AbortSignal }).timeout;
  return typeof timeout === 'function' ? timeout.call(AbortSignal, ms) : undefined;
}

/** True for what a runtime throws when its own deadline fires. */
function isTimeout(error: unknown): boolean {
  const name = (error as { name?: string } | null)?.name;
  return name === 'TimeoutError' || name === 'AbortError';
}

/** §1.6's shape, for a failure that never reached the point of having a body. */
function noAnswer(ms: number): ApiClientError {
  return new ApiClientError(0, {
    code: 'NO_ANSWER',
    message:
      `The server did not answer within ${Math.round(ms / 1000)} seconds. ` +
      `Nothing was changed. Try again, and if it keeps happening the service ` +
      `is not reachable from here.`,
  });
}

export interface ApiErrorBody {
  readonly code: string;
  readonly message: string;
  readonly rule_id?: string;
  readonly details?: Record<string, unknown>;
}

/**
 * API Spec §1.6 and Rule Book §0.8d — the rule that caused the rejection,
 * carried intact to whoever displays it.
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly ruleId: string | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = body.code;
    this.ruleId = body.rule_id;
    this.details = body.details;
  }

  /**
   * True when the operator's answer is a rule, not a mistake by the caller.
   *
   * The portal words these differently: a rule refusal gets the rule's own
   * explanation and a reference, a plain 400 gets "check what you entered".
   */
  get isRuleRefusal(): boolean {
    return this.ruleId !== undefined;
  }
}

/** The session is gone. Distinct from every other failure because the portal's
 *  response is to sign the affiliate in again, not to show them an error. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Your session has ended. Sign in again to continue.');
    this.name = 'SessionExpiredError';
  }
}

/**
 * Vite serves workspace packages with `?t=` cache-busters on re-exports.
 * Two copies of this module means `instanceof SessionExpiredError` is false
 * for a real 401 — and every screen then lies with "Could not reach the
 * server". Match by name/shape instead.
 */
export function isSessionExpiredError(error: unknown): boolean {
  return (
    error instanceof SessionExpiredError ||
    (typeof error === 'object' &&
      error !== null &&
      (error as { name?: string }).name === 'SessionExpiredError')
  );
}

export function isApiClientError(error: unknown): error is ApiClientError {
  if (error instanceof ApiClientError) return true;
  if (typeof error !== 'object' || error === null) return false;
  const e = error as { name?: string; status?: unknown; code?: unknown; message?: unknown };
  return e.name === 'ApiClientError' && typeof e.status === 'number' && typeof e.message === 'string';
}

/**
 * §21.4a — which rate a figure was converted at, and so whether it can move.
 *
 * Restated here rather than imported from `api-core`, the same way every other
 * report row is: a console reads the wire, not the server's internals.
 */
export type FxRatePolicy = 'FROZEN' | 'LATEST' | 'AVERAGE' | 'IDENTITY';

/** §21.3b — where the rate came from, in the order the rule prefers them. */
export type FxSourceKind = 'BRAND' | 'OPERATOR_PROVIDER' | 'PLATFORM_DEFAULT' | 'IDENTITY';

/**
 * §21.4 — *when* the operator asked for the rate to be taken
 * (`finance.fx_rate_timing`), which is a different sentence from what this
 * figure actually used. A settled period reports `FROZEN` whatever the timing
 * says, and a reader who cannot see both cannot tell "the rule chose the
 * frozen rate" from "the platform ignored my setting".
 */
export type FxRateTimingPolicy = 'TRANSACTION_TIME' | 'PERIOD_END' | 'PERIOD_AVERAGE';

/**
 * §21.3a at the display boundary — one rate, with everything §21.7b requires
 * the reader to be shown beside the number it produced.
 *
 * The ratio arrives twice: exactly, as the integer pair it is stored as, and
 * as a decimal string the console prints. The console prints the decimal and
 * never does arithmetic of its own on either — a rate recomputed in a browser
 * is a second implementation of §21.6's rounding, which is how a screen comes
 * to disagree with the statement it is describing.
 */
export interface FxRateNote {
  readonly from_currency: string;
  readonly to_currency: string;
  readonly rate_numerator: string;
  readonly rate_denominator: string;
  /** The same ratio to 10 decimal places, half-up. Display only. */
  readonly rate: string;
  readonly source: string;
  /** null for a frozen rate: the freeze does not keep §21.3b's provenance. */
  readonly source_kind: FxSourceKind | null;
  readonly as_of: string;
  readonly policy: FxRatePolicy;
}

/** One converted figure in `data`, and every rate that went into it. */
export interface ConversionNote {
  /** The field in `data` this describes, e.g. `ngr_minor`. */
  readonly figure: string;
  readonly to_currency: string;
  /** `MIXED` when the rows behind one total did not all use the same policy. */
  readonly policy: FxRatePolicy | 'MIXED';
  /**
   * §21.7b — the rate policy §21.4 was configured with, beside what this
   * figure actually used. Optional here and not on the wire: a console
   * compiled against an older API must not blank a report over a field the
   * server did not send yet.
   */
  readonly rate_timing?: FxRateTimingPolicy;
  readonly rates: readonly FxRateNote[];
}

/** §39.4 / §39.3a — the envelope every report arrives in, kept intact. */
export interface Envelope<T> {
  readonly data: T;
  readonly data_as_of: string;
  readonly is_provisional: boolean;
  readonly provisional_because?: string;
  /**
   * §21.4a — the figures are in the currency each was computed in, because no
   * published rate carries them into the one asked for. Said rather than
   * refused: a brand that changes its reporting currency keeps its history in
   * the old one, and a screen that answers that with an error shows nothing.
   */
  readonly not_converted?: {
    readonly currencies: readonly string[];
    readonly to: string;
    readonly because: string;
  };
  /**
   * §21.7 / §21.7b — present only when some figure in `data` was converted for
   * display, one entry per converted figure. Absent means nothing was
   * converted and every figure is in the currency it was computed in.
   */
  readonly conversions?: readonly ConversionNote[];
  /**
   * §21.7 — sent when the reader asked for a display currency: true when it
   * moved at least one figure, false when it moved none. False beside
   * `conversions` means every one of them is the report's own — a total over
   * more than one currency is added up in one — and the original-currency
   * view is this same screen. Optional for the reason `rate_timing` is.
   */
  readonly converted_for_reader?: boolean;
  /**
   * The placement filters (campaign, country, product, sub-ID and so on)
   * this report could not apply, by their query-string names, and a sentence
   * saying which figures they left whole. Absent when every filter set was
   * applied. A screen shows the sentence; it never drops the filter silently.
   */
  readonly filters_not_applied?: readonly string[];
  readonly filters_note?: string;
  /** Commission narrowed by a placement filter leaves affiliate-level lines out, and says so here. */
  readonly commission_note?: string;
  /**
   * Performance and funnel: revenue share names no player, so a grouping it
   * cannot be split by reports it beside the rows, one total per currency.
   */
  readonly revenue_share_split?: boolean;
  readonly revenue_share_unsplit?: readonly {
    readonly currency: string;
    readonly commission_minor: string;
    readonly commission_other_currency_lines: number;
  }[];
}

export interface ClientOptions {
  readonly baseUrl: string;
  readonly token: () => string | undefined;
  readonly fetch?: typeof globalThis.fetch;
}

/**
 * Native `window.fetch` must be called as a method of `Window`. Storing
 * `globalThis.fetch` on a field and invoking it as `this.#fetch(...)` sets
 * `this` to the client — Safari/Chrome then throw
 * `TypeError: Failed to execute 'fetch' on 'Window': Illegal invocation`.
 */
export function boundFetch(doFetch: typeof globalThis.fetch = globalThis.fetch): typeof globalThis.fetch {
  return (input, init) => doFetch.call(globalThis, input, init);
}

/**
 * The transport, and nothing else.
 *
 * Each portal subclasses this and declares its own endpoints. A shared client
 * carrying every app's routes is how an affiliate build ends up shipping the
 * admin surface — the methods are dead code there, but the URLs are not, and
 * the first person to read the bundle learns the whole API.
 */
export class HttpClient {
  /**
   * §39 — a report as a file.
   *
   * Fetched rather than linked, still — the session now rides an `HttpOnly`
   * cookie (`credentials: 'include'` carries it), but a plain `<a href>`
   * still can't set the CSRF header, and a `fetch()` + blob download is no
   * more costly than it already was. server.ts only requires that header on
   * a write, so it's sent here purely as a consistent habit, not a
   * requirement for a GET. The Bearer header is kept too, for a caller with
   * no cookie jar.
   */
  /**
   * Say why a file did not arrive.
   *
   * `download()` and `openInNewTab()` return nothing and are called for their
   * effect, so every call site in every portal is `void api.download(...)`
   * with no `.catch`. A refusal therefore reached nobody: it became an
   * unhandled rejection in a console the reader does not have open, the tab
   * that had been opened for it closed itself, and the button looked like it
   * had simply done nothing.
   *
   * That is how the affiliate statement PDF stayed broken. The route answered
   * 404 on every press, for months, and the portal had no way to say so.
   *
   * Reported here rather than at the call sites: there is nothing for a caller
   * to do with the error that this does not already do, and a rule kept in one
   * place is one a sixth call site cannot forget. Neither method rejects any
   * more, for the same reason — a rejection nobody awaits is not a report.
   */
  #sayFileFailed(e: unknown): void {
    showErrorToast(
      e instanceof Error && e.message.trim() !== ''
        ? stripRuleReferences(e.message)
        : 'That file could not be fetched.',
    );
  }

  async download(path: string, params: Record<string, string>): Promise<void> {
    const url = resolveRequestUrl(this.#baseUrl, path);
    for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);

    const token = this.#token();
    const fileDeadline = deadline(FILE_TIMEOUT_MS);
    let response: Response;
    try {
      response = await this.#fetchOrSayNoAnswer(url.toString(), FILE_TIMEOUT_MS, {
        credentials: 'include',
        headers: {
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          [CSRF_HEADER]: CSRF_VALUE,
        },
        ...(fileDeadline ? { signal: fileDeadline } : {}),
      });
      if (!response.ok) {
        throw new ApiClientError(response.status, stripRuleReferencesDeep(await response.json()) as ApiErrorBody);
      }
    } catch (e) {
      this.#sayFileFailed(e);
      return;
    }

    const blob = await response.blob();
    // The server named the file; it knows what the report is and when it was
    // taken. A name invented here is one that disagrees with the header.
    const disposition = response.headers.get('content-disposition') ?? '';
    const named = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'report.csv';

    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = named;
    link.click();
    // Revoked, or the blob is held for the life of the document — which on a
    // portal somebody leaves open all day is the whole day.
    URL.revokeObjectURL(href);
  }

  /**
   * §39 — a report as a file, viewed rather than saved.
   *
   * Same fetch as `download()` — the same auth headers apply to a PDF as to
   * any other report — but opened in a new tab instead of triggering an
   * `<a download>` save-to-disk. A statement PDF is something an affiliate
   * usually wants to glance at first, not silently receive into Downloads.
   *
   * The tab is opened *first*, synchronously, still inside the click that
   * triggered this call — not after the `await fetch`/`await blob()` below.
   * A `window.open()` called once those have resolved is no longer inside
   * the original user gesture as far as the browser's popup blocker is
   * concerned, and Safari in particular silently blocks it — no error, the
   * button just does nothing, which is indistinguishable from a fetch that
   * failed. Navigating an already-open blank tab to the blob URL once it's
   * ready sidesteps that entirely.
   */
  async openInNewTab(path: string, params: Record<string, string>): Promise<void> {
    const tab = window.open('', '_blank');

    const url = resolveRequestUrl(this.#baseUrl, path);
    for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);

    const token = this.#token();
    const fileDeadline = deadline(FILE_TIMEOUT_MS);
    let response: Response;
    try {
      // The same deadline as download(): a blank tab held open on a fetch
      // that never answers is a window the reader has to close themselves,
      // with nothing in it to say why.
      response = await this.#fetchOrSayNoAnswer(url.toString(), FILE_TIMEOUT_MS, {
        credentials: 'include',
        headers: {
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          [CSRF_HEADER]: CSRF_VALUE,
        },
        ...(fileDeadline ? { signal: fileDeadline } : {}),
      });
    } catch (e) {
      tab?.close();
      this.#sayFileFailed(e);
      return;
    }
    if (!response.ok) {
      tab?.close();
      this.#sayFileFailed(
        new ApiClientError(response.status, stripRuleReferencesDeep(await response.json()) as ApiErrorBody),
      );
      return;
    }

    const blob = await response.blob();
    const href = URL.createObjectURL(blob);
    // Revoked, not here — the tab still needs it after this function returns.
    if (tab) tab.location.href = href;
    // The pre-opened tab itself was blocked (rarer, but possible depending on
    // browser settings) — this attempt carries the same gesture-timing risk
    // `download()`'s own comment already accepts for its blob URL, but it is
    // strictly better than doing nothing.
    else window.open(href, '_blank');
  }

  /**
   * A file this session may read, as a Blob — for a thumbnail drawn on the
   * page rather than opened in a tab. Throws on any failure and says nothing:
   * a thumbnail that cannot load shows its file tile instead, which is not an
   * error the reader has to be told about.
   */
  async fetchBlob(path: string): Promise<Blob> {
    const url = resolveRequestUrl(this.#baseUrl, path);
    const token = this.#token();
    const response = await this.#fetchOrSayNoAnswer(url.toString(), FILE_TIMEOUT_MS, {
      credentials: 'include',
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        [CSRF_HEADER]: CSRF_VALUE,
      },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
  }

  /** §3.7 — revoked, not left to idle out. */
  logout(): Promise<unknown> {
    return this.request('POST', '/v1/logout');
  }

  /**
   * Boot-time cookie probe — confirms a session exists without loading the
   * permission set. The sign-in screen must not call `/v1/me/permissions`;
   * permissions are fetched only after a session is established (login
   * response or the SignedIn refresh below).
   */
  session(): Promise<{ authenticated: true }> {
    /*
     * Shorter than a screen's deadline, because the whole console waits on
     * this one: nothing renders until it settles, and useLoad's probe retries
     * once, so the default would leave somebody on loading skeletons for a
     * minute before the sign-in form appeared. Ten seconds is far longer than
     * a cookie check ever takes and short enough that an outage reaches the
     * form quickly.
     */
    return this.request('GET', '/v1/session', { timeoutMs: SESSION_TIMEOUT_MS });
  }

  /**
   * §3.2, §3.6 — read on every load rather than trusted from the sign-in
   * response, so a revoked role takes effect at the next page and not at the
   * next login. Time-boxed elevation depends on exactly that.
   */
  permissions(): Promise<{
    permissions: string[];
    user_id?: string;
    /** Present only in a platform user's own session: which writes it may still make. */
    self_session?: { team: boolean; details: boolean };
  }> {
    return this.request('GET', '/v1/me/permissions');
  }

  readonly #baseUrl: string;
  readonly #token: () => string | undefined;
  readonly #fetch: typeof globalThis.fetch;

  constructor(options: ClientOptions) {
    this.#baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.#token = options.token;
    this.#fetch = boundFetch(options.fetch ?? globalThis.fetch);
  }

  /**
   * The fetch, with a fired deadline turned into something a screen can show.
   *
   * A raw `TimeoutError` reaches `useLoad`, which has an `isAbortError` guard
   * that drops it — the screen would go back to waiting for ever, which is
   * the behaviour this exists to end. An ApiClientError is what every error
   * path in the consoles already knows how to render.
   */
  async #fetchOrSayNoAnswer(url: string, budget: number, init: RequestInit): Promise<Response> {
    try {
      return await this.#fetch(url, init);
    } catch (error) {
      if (isTimeout(error)) throw noAnswer(budget);
      throw error;
    }
  }

  async request<T>(
    method: string,
    path: string,
    init: {
      query?: Record<string, string | undefined>;
      body?: unknown;
      /** Override the read deadline. Ignored on a write — see below. */
      timeoutMs?: number;
      /** Extra request headers, e.g. the reason a platform direct edit carries. */
      headers?: Record<string, string>;
    } = {},
  ): Promise<T> {
    const url = resolveRequestUrl(this.#baseUrl, path);
    for (const [k, v] of Object.entries(init.query ?? {})) {
      if (v !== undefined && v !== '') url.searchParams.set(k, v);
    }

    const token = this.#token();
    const headers: Record<string, string> = { ...(init.headers ?? {}), accept: 'application/json' };
    if (token) headers['authorization'] = `Bearer ${token}`;
    if (init.body !== undefined) headers['content-type'] = 'application/json';
    // A cookie attaches itself; this header is the proof a same-site fetch
    // call sent the request, not a form on somebody else's page (server.ts's
    // own comment on the check this answers).
    if (method !== 'GET') headers[CSRF_HEADER] = CSRF_VALUE;

    /*
     * A deadline on a read, and deliberately none on a write.
     *
     * Abandoning a request client-side does not un-apply it server-side. A
     * write that is told "it failed" when it may well have landed is worse
     * than a spinner: somebody raises the adjustment again, or pays the
     * invoice twice. A read has no such ambiguity — nothing happened — so it
     * is the one that gets a deadline.
     */
    const budget = init.timeoutMs ?? READ_TIMEOUT_MS;
    const signal = method === 'GET' ? deadline(budget) : undefined;

    const response = await this.#fetchOrSayNoAnswer(url.toString(), budget, {
      method,
      headers,
      credentials: 'include',
      ...(signal ? { signal } : {}),
      /*
       * Never from the cache, and never into it. A console re-reads a list
       * the moment it changes something, and the same URL served from the
       * browser's own cache is what makes that read return what the screen
       * already had — the change looks lost until the page is reloaded. The
       * API says `no-store` for the same reason; this is the half that does
       * not depend on a proxy in between having honoured it.
       */
      cache: 'no-store',
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    });

    if (response.status === 401) {
      // Each console's root answers this by going straight back to sign-in.
      maybeNotifySessionExpired(path);
      throw new SessionExpiredError();
    }

    // §4.7 — a disabled module answers 404, deliberately indistinguishable from
    // a path that does not exist, so the portal must not translate 404 into
    // "this feature is turned off for you". That sentence is the disclosure the
    // status code was chosen to avoid.
    const text = await response.text();
    const parsed: unknown = text === '' ? null : safeJson(text);

    if (!response.ok) {
      if (isErrorBody(parsed)) throw new ApiClientError(response.status, parsed);
      throw new ApiClientError(response.status, {
        code: 'UNEXPECTED',
        message: `The server answered ${response.status}.`,
      });
    }

    maybeNotifyRequestSuccess(method, path, parsed);

    return parsed as T;
  }

}

/** Coerce query values to strings for URL search params. */
export function queryParams(
  values: Record<string, string | number | boolean | undefined>,
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) out[key] = String(value);
  }
  return out;
}

/**
 * Absolute `http://localhost:5173/api` breaks the moment somebody opens the
 * same Vite app as `http://127.0.0.1:5173` — cross-origin, no CORS on
 * api-core, every fetch becomes "Could not reach the server", and the
 * session cookie is scoped to the wrong host so a reload looks signed out.
 * A relative `/api` always matches the page origin; resolve it here.
 */
export function resolveRequestUrl(baseUrl: string, path: string): URL {
  const joined = `${baseUrl.replace(/\/+$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
  if (/^[a-z][a-z0-9+.-]*:/i.test(joined)) return new URL(joined);
  const origin =
    typeof globalThis.location?.origin === 'string' && globalThis.location.origin !== 'null'
      ? globalThis.location.origin
      : 'http://localhost';
  return new URL(joined, origin);
}

/**
 * Sign in, without a session.
 *
 * Separate from `request` because it is the one call that must work with no
 * token, and must return the raw status and body rather than throwing: §43's
 * refusals are 401s that the screen turns into one message, and an exception
 * here would make the second factor look like an error.
 */
export async function postLogin(
  baseUrl: string,
  body: { email: string; password: string; code?: string; portal?: 'affiliate' | 'operator' | 'platform' },
  doFetch: typeof globalThis.fetch = globalThis.fetch,
): Promise<{ status: number; body: unknown }> {
  const response = await boundFetch(doFetch)(resolveRequestUrl(baseUrl, '/v1/login').toString(), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  return { status: response.status, body: text === '' ? null : safeJson(text) };
}

/**
 * The onboarding calls, which all happen before a session exists.
 *
 * Grouped as one small client rather than methods on `HttpClient`, because a
 * class whose whole purpose is carrying a token would be a confusing home for
 * three calls that deliberately have none.
 */
export function onboardingApi(
  baseUrl: string,
  doFetch: typeof globalThis.fetch = globalThis.fetch,
) {
  const run = boundFetch(doFetch);
  const post = async (path: string, body: unknown): Promise<Record<string, unknown>> => {
    const response = await run(resolveRequestUrl(baseUrl, path).toString(), {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', accept: 'application/json', [CSRF_HEADER]: CSRF_VALUE },
      body: JSON.stringify(body),
    });
    const text = await response.text();
    const parsed = (text === '' ? {} : safeJson(text)) as Record<string, unknown>;
    if (!response.ok) throw new ApiClientError(response.status, parsed as never);
    maybeNotifyRequestSuccess('POST', path, parsed);
    return parsed;
  };
  const get = async (path: string): Promise<Record<string, unknown>> => {
    const response = await run(resolveRequestUrl(baseUrl, path).toString(), {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
    const text = await response.text();
    const parsed = (text === '' ? {} : safeJson(text)) as Record<string, unknown>;
    if (!response.ok) throw new ApiClientError(response.status, parsed as never);
    return parsed;
  };

  return {
    // §50.1 — the active reference-data currencies, for the signup form's
    // base-currency picker. The same rows /v1/admin/currencies serves.
    currencies: () =>
      get('/v1/signup/currencies') as Promise<{
        items: { code: string; name: string; minor_unit_scale: number }[];
      }>,
    enrol: (body: { email: string; password: string }) =>
      post('/v1/mfa/enrol', body) as Promise<{ secret: string; uri: string; ticket: string; message: string }>,
    confirm: (body: { email: string; code: string; ticket: string }) =>
      post('/v1/mfa/confirm', body) as Promise<{ enrolled: boolean }>,
    changePassword: (body: { email: string; current_password: string; new_password: string }) =>
      post('/v1/password', body) as Promise<{ changed?: boolean; message?: string }>,
    // §50.1 — a sandbox without a sales call. No session, by definition.
    signUp: (body: {
      legal_name: string; display_name: string; base_currency: string;
      /** Item 3 (third batch) — ISO 3166-1 alpha-2, e.g. "GB". */
      country: string;
      email: string; password: string; owner_name?: string; owner_phone?: string;
      /** The operator's own word that it holds every licence it needs — recorded, not checked. */
      licences_attested: boolean;
    }) => post('/v1/signup', body) as Promise<{ status: string; message: string }>,
    confirmDomain: (body: { token: string }) =>
      post('/v1/signup/confirm', body) as Promise<{ verified: boolean; message: string }>,
  };
}

/**
 * Every answer a portal reads is parsed here, and leaves without its Rule Book
 * citations (`stripRuleReferencesDeep`) — the one place no screen can bypass.
 */
function safeJson(text: string): unknown {
  try {
    return stripRuleReferencesDeep(JSON.parse(text));
  } catch {
    // A proxy returning an HTML error page is the usual cause. Reported as an
    // unexpected answer rather than crashing the render.
    return null;
  }
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { code?: unknown }).code === 'string' &&
    typeof (value as { message?: unknown }).message === 'string'
  );
}

/**
 * What to put on the screen when a request fails.
 *
 * API Spec §1.6 and Rule Book §0.8d again — "support answers disputes by
 * reading those IDs back", so the rule id is shown rather than logged. An
 * affiliate told "commission cannot be recalculated for a locked period (§20.6)" can look it up, quote it
 * to their account manager, and stop asking. One told "operation failed" opens
 * a ticket.
 */
export function explainFailure(error: unknown): { message: string; reference?: string } {
  if (isSessionExpiredError(error)) {
    return {
      message:
        error instanceof Error
          ? error.message
          : 'Your session has ended. Sign in again to continue.',
    };
  }
  if (isApiClientError(error)) {
    /*
     * The no-information 404.
     *
     * `moduleDisabled()` (api-core/http.ts) answers a request for something
     * this operator has not switched on with a bare "Not found." — deliberately,
     * because a 403 would tell whoever is asking what the operator has bought
     * and configured. That is right for an API caller and useless to a reader,
     * who sees a red box saying three words on a report they just opened.
     * Reported on 2026-09-28 from Reports → Breakdown.
     *
     * Matched on the exact bare message, so a 404 that says which record is
     * missing keeps saying it: anything context-specific is longer than this.
     */
    if (error.status === 404 && error.message.trim() === 'Not found.') {
      return {
        message:
          'This is not switched on for your account. Ask whoever manages your '
          + 'platform settings if you need it.',
      };
    }
    // AF-14 — a refused password names its reason; say it in the reader's
    // language rather than the server's English.
    if (error.code === 'WEAK_PASSWORD') {
      const text = passwordRefusalText(error.details?.['reason']);
      if (text) return { message: text };
    }
    return {
      message: stripRuleReferences(error.message),
    };
  }
  // A network failure is not the server's answer, and saying so stops an
  // affiliate reporting an outage that is their own connection. Include the
  // underlying browser message when present — "Failed to fetch" vs a CORS
  // refusal are different fixes, and the generic line alone hides which.
  const detail =
    error instanceof Error && error.message && error.message !== 'Failed to fetch'
      ? ` (${error.name}: ${error.message})`
      : error instanceof Error && error.name
        ? ` (${error.name})`
        : '';
  return {
    message: `Could not reach the server. Check your connection and try again.${detail}`,
  };
}
